import React, {useMemo} from 'react';

import isArchived from 'archive/isArchived';
import {OBJECTS_CREATABLE_PERMISSION} from 'auth/PermissionConstants';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import QueryingResultSchemaSelector from 'results/ResultSchemaSelector';
import Stepper, {StepperStep} from 'ui-library/stepper/Stepper';

import ColumnMappingTable from './column-mapping-table/ColumnMappingTable';
import {DataFrameColumnToFieldMapping, DataMappingState} from './DataMappingTypes';
import validateSchemaMapping from './validateDataMapping';

export interface SaveToResultsStepperProps {
  resultSchema: ResultSchema | null;
  inputDataFrameSpec: {name: string; columns: Array<DataFrameColumnDef>};
  columnMapping: Array<DataFrameColumnToFieldMapping>;
  onSchemaChange: (newSchema: ResultSchema | null) => void;
  onMappingsChange: (newMappings: Array<DataFrameColumnToFieldMapping>) => void;
  disabled?: boolean;
}

export default function SaveToResultsStepper({
  resultSchema,
  inputDataFrameSpec,
  columnMapping,
  onSchemaChange,
  onMappingsChange,
  disabled,
}: SaveToResultsStepperProps): JSX.Element {
  const dataMappingState: DataMappingState = useMemo(
    () => ({resultSchema, mappings: columnMapping}),
    [resultSchema, columnMapping]
  );

  const validationErrors = validateSchemaMapping(dataMappingState);

  return (
    <Stepper>
      <StepperStep title="Select Result schema" id="schema">
        <QueryingResultSchemaSelector
          selectedSchema={resultSchema}
          onChange={onSchemaChange}
          filter={(rs) => !isArchived(rs)}
          requiredPermissions={[OBJECTS_CREATABLE_PERMISSION]}
          isDisabled={disabled}
        />
      </StepperStep>
      {resultSchema != null && (
        <StepperStep title="Map columns to Results" id="map">
          <ColumnMappingTable
            inputDataFrameSpec={inputDataFrameSpec}
            outputResultSchema={resultSchema}
            mappings={columnMapping}
            setMappings={onMappingsChange}
            errors={validationErrors}
          />
        </StepperStep>
      )}
    </Stepper>
  );
}
